import React, { useMemo } from "react";
import { FiActivity, FiCalendar, FiClipboard, FiCoffee } from "react-icons/fi";
import { Chip } from "./Chip";
import { normalizePlanShare } from "../../utils/commsPlanShare";

/**
 * Per-plan-kind icon + label. Full literal class strings: `tailwind.config.js`
 * only safelists the `coach-0..7` palette, so anything built by concatenation
 * outside that pattern is purged from the production bundle.
 */
const PLAN_KIND = {
  WORKOUT: {
    Icon: FiActivity,
    label: "Workout plan",
    iconClass: "text-orange-primary",
  },
  DIET: {
    Icon: FiCoffee,
    label: "Diet plan",
    iconClass: "text-success",
  },
};

const FALLBACK_KIND = {
  Icon: FiClipboard,
  label: "Plan",
  iconClass: "text-gray-500",
};

/**
 * Matches AttachmentGrid's bordered row, so cards and files share a surface.
 *
 * `bg-gray-100`, not the `bg-gray-50` AttachmentGrid writes: `tailwind.config.js`
 * sets `gray` to a flat `#D9D9D9` and then declares the scale steps it wants
 * individually, so the default `gray-50` does not exist and that class compiles
 * to nothing. `gray-100` (#F3F4F6) is the nearest step that is actually defined.
 */
const ROW_CLASS =
  "mt-2 flex w-full max-w-[260px] items-start gap-2 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-left text-xs text-gray-700";

/**
 * One chip label per movement, e.g. ["10 workouts added", "3 sets updated"].
 * Reads better than folding several movements into one chip.
 *
 * @param {import('../../utils/commsPlanShare').PlanCount[]} counts
 * @returns {string[]}
 */
const describePlanCounts = (counts) =>
  counts.flatMap((count) =>
    [
      ["added", count.added],
      ["updated", count.updated],
      ["removed", count.removed],
    ]
      .filter(([, n]) => n > 0)
      .map(
        ([verb, n]) => `${n} ${count.label}${n === 1 ? "" : "s"} ${verb}`
      )
  );

/**
 * Format a plan's schedule window as "Aug 18 – Sep 14". Returns "" when the
 * window is absent or unparseable — a bad date must not blank the whole card.
 *
 * @param {string|null} from
 * @param {string|null} to
 * @returns {string}
 */
const formatWindow = (from, to) => {
  const label = (iso) => {
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  };
  const start = label(from);
  const end = label(to);
  if (start && end) return `${start} – ${end}`;
  return start || end;
};

/**
 * The plan(s) a `WORKOUT_CARD` / `DIET_CARD` message refers to, rendered
 * beneath the message's own sentence.
 *
 * Presentational only, and deliberately non-tappable: a WORKOUT `plan_id` is a
 * `TargetPlans.id` while `/dashboard/:id/workout-plan/:durationId` expects a
 * duration id, so a link would open the wrong plan or 404. Everything shown
 * here comes straight from the message payload.
 *
 * Renders nothing when the message isn't a plan card, so callers can mount it
 * unconditionally — same contract as `MessageMedia`.
 *
 * @param {Object} props
 * @param {Object} props.message - Raw server (or optimistic) message.
 */
export const PlanShareCard = ({ message }) => {
  const planShare = useMemo(() => normalizePlanShare(message), [message]);

  if (!planShare) return null;

  return (
    <div className="mt-1">
      {planShare.plans.map((plan) => {
        const kind = PLAN_KIND[plan.planKind] || FALLBACK_KIND;
        const { Icon } = kind;
        const window = formatWindow(plan.from, plan.to);

        return (
          <div key={plan.id} className={ROW_CLASS}>
            <Icon
              aria-hidden="true"
              size={14}
              className={`mt-0.5 flex-shrink-0 ${kind.iconClass}`}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-gray-900">
                {plan.title || kind.label}
              </p>
              {plan.title && (
                <p className="truncate text-[11px] text-gray-500">
                  {kind.label}
                </p>
              )}

              {plan.counts.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {describePlanCounts(plan.counts).map((text) => (
                    <Chip key={text} tone="brand" className="!px-2 !py-0.5">
                      {text}
                    </Chip>
                  ))}
                </div>
              )}

              {window && (
                <p className="mt-1.5 flex items-center gap-1 text-[11px] text-gray-500">
                  <FiCalendar aria-hidden="true" size={11} />
                  <span>{window}</span>
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PlanShareCard;
